<template>
  <DeepDialog
    v-model="isDialogOpen"
    card-text-fluid
    fullscreen-on-mobile
    persistent
    :title="dialogTitle"
    width="600"
    @keydown.esc="onCancel"
  >
    <!-- The signature-level explainer belongs to that step only; it renders in
         the title actions so it sits just left of the close button. -->
    <template #card-title-content-actions-prepend>
      <SignatureModeInfoMenu v-if="!isParticipantsStep" />
    </template>
    <template #card-title-content-actions-append
      ><div class="mr-1"></div
    ></template>
    <!-- The wizard's progress lives in the title block (as a subtitle) instead
         of a stepper above the form. -->
    <template #card-title-content-title>
      <div class="signing-workflow-title">
        <div class="signing-workflow-title__text">{{ dialogTitle }}</div>
        <div v-if="dialogSubtitle" class="signing-workflow-title__subtitle">
          {{ dialogSubtitle }}
        </div>
      </div>
    </template>

    <template #content>
      <!-- While the confirm/reconcile calls are in flight the whole form is
           inert: blocks pointer, keyboard and focus for the entire subtree
           (inputs, drag handles, switches) in one place, so no edit can race
           the batched persistence. -->
      <div
        class="signing-workflow-content"
        :class="{ 'signing-workflow-content--confirming': isConfirming }"
        :inert="isConfirming"
      >
        <SignatureModeStep v-if="step === 1" />
        <ParticipantsStep v-else />
      </div>
    </template>

    <template #actions>
      <!-- Wizard: step navigation -->
      <template v-if="isWizard">
        <VBtnSecondary
          v-if="step === 1"
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-close"
          :disabled="isConfirming"
          :text="i18n.t('buttons.close')"
          variant="outlined"
          @click="onCancel"
        />
        <VBtnSecondary
          v-else
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-back"
          :disabled="isConfirming"
          :text="i18n.t('buttons.back')"
          variant="outlined"
          @click="goBack()"
        />

        <VBtnPrimary
          v-if="step === 1"
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-continue"
          :disabled="isConfirming"
          :text="i18n.t('buttons.continue')"
          @click="goNext()"
        />
        <VBtnPrimary
          v-else
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-confirm"
          :loading="isConfirming"
          :text="i18n.t('buttons.confirm')"
          @click="onConfirm"
        />
      </template>

      <!-- Single-section edit: cancel + confirm -->
      <template v-else>
        <VBtnSecondary
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-cancel"
          :disabled="isConfirming"
          :text="i18n.t('buttons.quorum_cancel')"
          variant="outlined"
          @click="onCancel"
        />
        <VBtnPrimary
          :class="{ 'w-100': display.xs.value }"
          data-test-id="signing-workflow-confirm"
          :loading="isConfirming"
          :text="i18n.t('buttons.confirm')"
          @click="onConfirm"
        />
      </template>
    </template>
  </DeepDialog>
</template>

<script lang="ts">
// Documents whose setup wizard we've already auto-opened this session. Kept at
// module scope (not per component instance) so a transient remount of the
// drawer while the document finishes loading can't reset the guard and re-fire
// the wizard — that caused a brief open/close/open flash on first load.
const autoOpenedDocuments = new Set<string>();
</script>

<script setup lang="ts">
import { computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify/framework';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow';
import { useDocumentWizard } from '@/composables/create/useDocumentWizard';
import { useDocumentContext } from '@/composables/useDocumentContext';
import SignatureModeStep from '@/components/dialogs/signing-workflow/SignatureModeStep.vue';
import SignatureModeInfoMenu from '@/components/dialogs/signing-workflow/SignatureModeInfoMenu.vue';
import ParticipantsStep from '@/components/dialogs/signing-workflow/ParticipantsStep.vue';
import { useCreateStore } from '@/stores/create';

const i18n = useI18n();
const display = useDisplay();
const {
  isOpen,
  step,
  isWizard,
  isConfirming,
  goNext,
  goBack,
  openWizard,
  cancel,
  confirm
} = useSigningWorkflow();

const createStore = useCreateStore();
const { currentDocument } = useDocumentContext();
const { shouldAutoOpen } = useDocumentWizard();

const TOTAL_STEPS = 2;

const isParticipantsStep = computed(() => step.value !== 1);

const dialogTitle = computed(() => {
  return isParticipantsStep.value
    ? i18n.t('titles.participants_step')
    : i18n.t('titles.signature_mode_step');
});

// Both steps always show their description (wizard and single-section edit
// alike); the wizard additionally prefixes its progress to it.
const dialogSubtitle = computed(() => {
  const description = isParticipantsStep.value
    ? i18n.t('texts.participants_step_description')
    : i18n.t('texts.signature_mode_step_description');

  if (isWizard.value) {
    const progress = i18n.t('texts.step_of', {
      current: step.value,
      total: TOTAL_STEPS
    });
    return `${progress} · ${description}`;
  }

  return description;
});

const isDialogOpen = computed({
  get: () => isOpen.value,
  set: (value: boolean) => {
    if (!value) {
      onCancel();
    }
  }
});

// While the confirm/reconcile API calls are in flight the dialog can't be
// dismissed (ESC, click-outside, Cancel/Close) — closing mid-reconcile would
// discard the draft and flip deferPersistence while requests are still running.
function onCancel() {
  if (isConfirming.value) return;
  cancel();
}

async function onConfirm() {
  if (isConfirming.value) return;
  await confirm();
}

// Auto-open the wizard while a document's setup is still pending — that gate
// lives in shouldAutoOpen(): a pending entry in the local preferences on the
// dashboard, or the absence of the localStorage completion flag on the public
// (ERP) route. A one-shot per document (tracked at module scope, see above) so
// cancelling doesn't re-nag within a session and a remount can't re-fire.
watch(
  () => currentDocument.value?.documentId,
  async () => {
    const documentId = currentDocument.value?.documentId;
    if (!documentId || autoOpenedDocuments.has(documentId)) return;
    if (!shouldAutoOpen(documentId)) return;
    // Claim the one-shot synchronously, then open after the current load churn
    // (document re-fetch, preference writes) settles so the dialog opens once
    // and stays open instead of flashing open/closed.
    autoOpenedDocuments.add(documentId);
    await nextTick();
    openWizard();
  },
  { immediate: true }
);

// The dialog can open before the document's signees are available — the DeepBox
// entry path creates the document and lets the create page load it after mount,
// so the first payload can still come back without them. The draft cloned on
// open is empty then and the participants step shows a blank field instead of
// the signees the document already has (e.g. the auto-added initiator), while
// Confirm has no snapshot of what was persisted. Rebuild the draft whenever the
// committed signees change, as long as the draft holds nothing the user typed.
// Keyed on the signees themselves (not the document reference) and immediate,
// so it also catches an in-place update and a dialog that was remounted — the
// auto-open watcher above is a one-shot per document and won't re-arm it.
// Never while confirming: confirm() deliberately leaves deferred mode before
// the dialog closes, and commits the draft into the document on the way.
watch(
  () =>
    createStore.committedSigneesOrdered
      .flat()
      .map((signee) => `${signee.signeeId}:${signee.email ?? ''}`)
      .join('|'),
  () => {
    if (!createStore.document || !isOpen.value || isConfirming.value) return;
    if (
      createStore.deferPersistence &&
      !createStore.isParticipantsDraftDiscardable()
    ) {
      return;
    }
    // Only the participants draft — re-running the dialog's full init() would
    // also reset the signature-mode draft and discard a selection the user
    // already made in step 1.
    createStore.deferPersistence = true;
    createStore.beginParticipantsDraft();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
// A fixed viewport for the form: the dialog keeps exactly one size across both
// steps and while the additional-settings panel expands or collapses, so the
// surrounding page never shifts and the footer buttons never move under the
// cursor. Overflow is owned here rather than by the dialog itself.
//
// The dialog renders card-text-fluid (no padding of its own) and the inset
// lives here instead, so this element's padding box is the scrollport: a
// full-bleed child (the additional-settings row) can then span the dialog edge
// to edge without spilling out of the scroll container and adding a horizontal
// scrollbar.
.signing-workflow-content {
  padding: 24px;
  height: 600px;
  max-height: calc(100vh - 260px);
  overflow-y: auto;
  overscroll-behavior: contain;
  transition: opacity 0.15s ease;
}

// Fullscreen on mobile: the sheet already owns the scrolling, so don't pin a
// desktop-sized box inside it.
@media (max-width: 599px) {
  .signing-workflow-content {
    height: auto;
    max-height: none;
    overflow: visible;
  }
}

// Dimmed while inert during confirm so the frozen form reads as locked, not
// broken (the Confirm button's spinner carries the progress signal).
.signing-workflow-content--confirming {
  opacity: 0.6;
}

.signing-workflow-title {
  display: grid;
  gap: 2px;
  min-width: 0;
  padding: 12px 8px;
}

.signing-workflow-title__text {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: rgb(var(--v-theme-grey-darken-4));
}

.signing-workflow-title__subtitle {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: rgb(var(--v-theme-grey-darken-1));
  white-space: normal;
}

:deep(.expert-setting--visually-hidden) {
  visibility: hidden;
  pointer-events: none;
}
</style>
